import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  TelemetryWorkspaceConfig,
  TelemetryPanelConfig,
  TelemetryDashboardContextState,
  TelemetryUnitSystem,
  TelemetryViewportState,
  TelemetryDataAdapter,
} from '../types/telemetry-dashboard.types';

const TelemetryDashboardContext = createContext<TelemetryDashboardContextState | null>(null);

export interface TelemetryDashboardProviderProps {
  children: ReactNode;
  initialWorkspaces?: TelemetryWorkspaceConfig[];
  adapter?: TelemetryDataAdapter;
}

export const TelemetryDashboardProvider: React.FC<TelemetryDashboardProviderProps> = ({
  children,
  initialWorkspaces = [
    {
      id: 'default-ws',
      name: 'Overview Workspace',
      panels: [],
    },
  ],
  adapter,
}) => {
  const [workspaces, setWorkspaces] = useState<TelemetryWorkspaceConfig[]>(initialWorkspaces);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(
    initialWorkspaces[0]?.id || null
  );
  const [timeframe, setTimeframe] = useState<number>(5);
  const [precision, setPrecision] = useState<number>(2);
  const [units, setUnits] = useState<TelemetryUnitSystem>({});
  const [expandedPanel, setExpandedPanel] = useState<TelemetryPanelConfig | null>(null);

  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId) || workspaces[0];
  const activeLayout = activeWorkspace?.panels || [];

  const setLayout = useCallback(
    (panels: TelemetryPanelConfig[]) => {
      if (!activeWorkspaceId) return;
      setWorkspaces((prev) =>
        prev.map((ws) => (ws.id === activeWorkspaceId ? { ...ws, panels } : ws))
      );
    },
    [activeWorkspaceId]
  );

  const patchPanel = useCallback(
    (id: string, patch: Partial<TelemetryPanelConfig>) => {
      setLayout(
        activeLayout.map((p) => (p.id === id ? { ...p, ...patch } : p))
      );
      if (adapter?.onPanelUpdate) {
        adapter.onPanelUpdate(id, patch);
      }
    },
    [activeLayout, setLayout, adapter]
  );

  const removePanel = useCallback(
    (id: string) => {
      setLayout(activeLayout.filter((p) => p.id !== id));
    },
    [activeLayout, setLayout]
  );

  const duplicatePanel = useCallback(
    (id: string) => {
      const existing = activeLayout.find((p) => p.id === id);
      if (!existing) return;
      const dup: TelemetryPanelConfig = {
        ...existing,
        id: `p-${Date.now()}`,
        name: `${existing.name} (Copy)`,
        y: existing.y + existing.h,
      };
      setLayout([...activeLayout, dup]);
    },
    [activeLayout, setLayout]
  );

  const switchWorkspace = useCallback((id: string) => {
    setActiveWorkspaceId(id);
  }, []);

  const setUnit = useCallback((key: string, value: string) => {
    setUnits((prev) => ({ ...prev, [key]: value }));
  }, []);

  const viewport: TelemetryViewportState = {
    spanMs: timeframe * 60000,
    offsetMs: 0,
  };

  const value: TelemetryDashboardContextState = {
    workspaces,
    activeWorkspaceId,
    activeLayout,
    setLayout,
    patchPanel,
    removePanel,
    duplicatePanel,
    switchWorkspace,
    timeframe,
    setTimeframe,
    precision,
    setPrecision,
    units,
    setUnit,
    viewport,
    viewportLabel: `${timeframe} min window · live`,
    isPaused: false,
    expandedPanel,
    setExpandedPanel,
  };

  return (
    <TelemetryDashboardContext.Provider value={value}>
      {children}
    </TelemetryDashboardContext.Provider>
  );
};

export const useTelemetryDashboardContext = () => {
  const ctx = useContext(TelemetryDashboardContext);
  if (!ctx) {
    throw new Error('useTelemetryDashboardContext must be used within TelemetryDashboardProvider');
  }
  return ctx;
};
